import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import { _ } from './util'

import type { OpUnitType, QUnitType } from 'dayjs'
import type { DateValue } from '../typings'

type DateFormatType = 'date' | 'full' | 'normal' | 'simple' | 'normal_simple'

const MILLISECONDS_IN_DAY = 86400000

export { dayjs }

/**
 * 格式化日期
 * @param dateVal 日期值（日期/数字/字符串类型）
 * @param template  日期格式
 */
export function format(dateVal?: DateValue, template?: DateFormatType | string): string {
  template = template || 'full'

  // 简单格式化
  if (template === 'simple' || template === 'full_simple') {
    return simpleFormat(dateVal, template === 'full_simple')
  }

  if (template === 'date') {
    template = 'YYYY-MM-DD'
  } else if (template === 'normal') {
    template = 'YYYY-MM-DD HH:mm'
  } else if (template === 'full' || template === 'datetime') {
    template = 'YYYY-MM-DD HH:mm:ss'
  } else if (template === 'dense') {
    template = 'YYYYMMDDHHmmss'
  }

  const dateStr = dayjs(dateVal).format(template)
  return dateStr
}

/**
 * 获取日期简单格式
 * @param dateVal 日期值
 * @param withTime
 * @returns
 */
export function simpleFormat(dateVal?: DateValue, withTime?: boolean): string {
  if (dateVal === undefined) {
    return ''
  }

  const d = new Date(dateVal)
  if (!d) return ''

  const nowDate = +new Date() // 当前日期数值
  // 日期差距，Math.floor是为了防止神秘的javascript计算问题
  const dateDiff = Math.floor((nowDate - d.valueOf()) / MILLISECONDS_IN_DAY)

  if (withTime === undefined && dateDiff < 3) {
    withTime = true
  }

  let t = 'YYYY年MM月DD日'
  if (withTime) {
    t = 'YYYY年MM月DD日 HH时mm'
  }

  if (dateDiff < 180) {
    t = 'MM月DD日'
    if (withTime) {
      t = 'MM月DD日 HH时mm'
    }
  }

  const dateStr = format(dateVal, t)
  if (dateStr === 'Invalid Date') return ''
  return dateStr
}

/** 解析并格式化 */
export function parseAndFormat(val: string | Date, template?: string) {
  const date = parse(val)
  return format(date, template)
}

export function startOf(val: string | Date, unit: OpUnitType = 'd') {
  const date = parseValidDate(val)
  if (!date) return null
  return dayjs(date).startOf(unit).toDate()
}

export function endOf(val: string | Date, unit: OpUnitType = 'd') {
  const date = parseValidDate(val)
  if (!date) return null
  return dayjs(date).endOf(unit).toDate()
}

// 今天
export function today() {
  return dayjs().startOf('d').toDate() as Date
}

// 昨天
export function yestoday() {
  return dayjs().startOf('d').subtract(1, 'd').toDate() as Date
}

// 明天
export function tomorrow() {
  return dayjs().startOf('d').add(1, 'd').toDate() as Date
}

/** 是否有效日期 */
export function parseValidDate(val: string | Date) {
  const date = parse(val)
  return isNaN(date.getTime()) ? null : date
}

/**
 * 解析日期字符串
 * @param val 字符串
 * @param format 格式
 * @param locale 本地化语言（需要导入dayjs语言）
 * @param strict 严格模式（要求格式和输入内容完全匹配，包括分隔符）
 * @returns
 */
export function parse(val: string | Date, format?: string, locale?: string, strict?: boolean): Date {
  if (!_.isString(val)) return new Date(val)

  const str = val.trim()

  if (!str) return new Date('')

  switch (str) {
    case 'now':
      return new Date()
    case 'today':
      return today()
    case 'yestoday':
      return yestoday()
    case 'tomorrow':
      return tomorrow()
  }

  // T时间
  if (str.startsWith('T')) {
    // ms必须放在前面，以免与s混淆
    const units = ['ms', 'd', 'w', 'M', 'Q', 'y', 'h', 'm', 's']
    const unit = units.find(u => str.endsWith(u))
    const duNum = parseFloat(str.substring(2, str.length - (unit?.length || 0)))
    const op = str[1]

    let opFn = dayjs()
    switch (op) {
      case '+':
        opFn = dayjs().add(duNum, unit as any)
        break
      case '-':
        opFn = dayjs().subtract(duNum, unit as any)
        break
    }

    return opFn.toDate()
  }

  // last时间
  if (str.startsWith('last')) {
    let duNum = 1

    const units = ['Day', 'Week', 'Month', 'Quarter', 'Year']
    const unit = (units.find(u => str.includes(u)) || 'Day').toLocaleLowerCase()

    if (str.endsWith('s')) {
      const duNumStr = str.substring(4, str.length - unit.length - 1)
      duNum = parseInt(duNumStr, 10)
    }

    return dayjs()
      .subtract(duNum, unit as any)
      .toDate()
  }

  const date = dayjs(str, format, locale, strict).toDate()
  return date
}

/**
 * 新增年
 * @param dateVal 日期值
 * @param years 年数
 * @returns
 */
export function addYears(dateVal: DateValue, years: number): Date {
  return dayjs(dateVal).add(years, 'y').toDate()
}

/**
 * 新增月
 * @param dateVal 日期值
 * @param months 月数
 * @returns
 */
export function addMonths(dateVal: DateValue, months: number): Date {
  return dayjs(dateVal).add(months, 'm').toDate()
}

/**
 * 新增日
 * @param dateVal 日期值
 * @param days 天数
 * @returns
 */
export function addDays(dateVal: DateValue, days: number): Date {
  return dayjs(dateVal).add(days, 'd').toDate()
}

/**
 * 新增时
 * @param dateVal 日期值
 * @param hours 小时数
 * @returns
 */
export function addHours(dateVal: DateValue, hours: number): Date {
  return dayjs(dateVal).add(hours, 'h').toDate()
}

/**
 * 新增分
 * @param dateVal 日期值
 * @param minutes 分钟数
 * @returns
 */
export function addMinutes(dateVal: DateValue, minutes: number): Date {
  return dayjs(dateVal).add(minutes, 'm').toDate()
}

/**
 * 新增秒
 * @param dateVal 日期值
 * @param seconds 秒数
 * @returns
 */
export function addSeconds(dateVal: DateValue, seconds: number): Date {
  return dayjs(dateVal).add(seconds, 's').toDate()
}

/**
 * 新增毫秒
 * @param dateVal 日期值
 * @param milliSeconds 毫秒数
 * @returns
 */
export function addMilliSeconds(dateVal: DateValue, milliSeconds: number): Date {
  return dayjs(dateVal).add(milliSeconds, 'ms').toDate()
}

/**
 * 日期差距
 * @param formDate
 * @param toDate
 * @param unit
 * @returns
 */
export function diff(formDate: DateValue, toDate: DateValue, unit?: QUnitType | OpUnitType) {
  return dayjs(formDate).diff(toDate, unit)
}

/**
 * 是否有效的时间
 * @param date
 * @returns
 */
export function isValid(date: any) {
  return !!date.getTime() && !isNaN(date.getTime())
}
